import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import { Chip, Tooltip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import { DataGrid } from "components/DataGrid";
import { DocumentType } from "generated-graphql/graphql";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";

// Document row interface for the unified table
interface PaymentDocumentRow {
  id: string;
  title: string;
  documentType: DocumentType;
  documentId: string;
  sourceType: "invoice" | "receipt";
  status: "saved" | "pending";
  canDelete: boolean; // Derived from status and permissions
}

interface PaymentDocumentsTableProps {
  // Payment data from the query
  payment?: {
    document?: {
      id: string;
      title: string;
      documentType: DocumentType;
      status?: "saved" | "pending";
    } | null;
    paymentInvoices?: Array<{
      id: string;
      invoiceDocument?: {
        id: string;
        title: string;
        documentType: DocumentType;
      } | null;
      status?: "saved" | "pending";
    }> | null;
  } | null;
  // Simplified props - behavior is now driven by document status
  allowDeletion?: boolean; // Whether deletion is allowed at all
  hideTitle?: boolean;
  titleOverride?: string;
  onRemoveDocument?: (documentId: string, sourceType: "invoice" | "receipt") => void;
}

export const PaymentDocumentsTable: React.FC<PaymentDocumentsTableProps> = ({
  payment,
  allowDeletion = false,
  hideTitle = false,
  titleOverride,
  onRemoveDocument,
}) => {
  const [getDocumentDownloadLink] = useDocumentDownloadLink();

  // Combine all payment documents into a unified data structure
  // This allows us to display invoices and receipts together in a single UI table
  // for better user experience and consistent document management
  const allDocuments: PaymentDocumentRow[] = [
    // Add receipt document if it exists
    ...(payment?.document
      ? [
          {
            id: `receipt-${payment.document.id}`,
            title: payment.document.title || "[No document attached]",
            documentType: payment.document.documentType,
            documentId: payment.document.id,
            sourceType: "receipt" as const,
            status: (payment.document.status || "saved") as "saved" | "pending",
            canDelete: allowDeletion,
          },
        ]
      : []),
    // Add invoice documents
    ...(payment?.paymentInvoices?.map((invoice, index) => ({
      id: `invoice-${invoice.id}`,
      title: invoice.invoiceDocument?.title || "[No document attached]",
      documentType: invoice.invoiceDocument?.documentType || DocumentType.Invoice,
      documentId: invoice.invoiceDocument?.id || "",
      sourceType: "invoice" as const,
      status: (invoice.status || "saved") as "saved" | "pending",
      canDelete: allowDeletion,
    })) || []),
  ];

  // Check if we should show the status column
  const shouldShowStatusColumn = allDocuments.some((doc) => doc.status === "pending");

  const columns: GridColDef<PaymentDocumentRow>[] = [
    {
      field: "title",
      headerName: "Title",
      flex: 1,
      valueGetter: (_, row) => row.title,
    },
    {
      field: "documentType",
      headerName: "Document Type",
      flex: 0.4,
      valueGetter: (_, row) => row.documentType,
    },
    // Show status column when there are pending documents
    ...(shouldShowStatusColumn
      ? [
          {
            field: "status",
            headerName: "Status",
            flex: 0.6,
            renderCell: (params: any) => (
              <Chip
                label={params.row.status === "saved" ? "Saved" : "Pending"}
                color={params.row.status === "saved" ? "success" : "info"}
                variant="outlined"
                size="small"
                sx={
                  params.row.status === "pending"
                    ? {
                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                        borderColor: "rgba(25, 118, 210, 0.3)",
                        color: "rgba(25, 118, 210, 0.87)",
                        fontWeight: 500,
                      }
                    : {}
                }
              />
            ),
          } as GridColDef<PaymentDocumentRow>,
        ]
      : []),
    {
      field: "actions",
      type: "actions",
      resizable: false,
      sortable: false,
      getActions: (params) => {
        const actions = [];

        // Download action - available for all documents with a valid documentId
        if (params.row.documentId) {
          actions.push(
            <Tooltip title="Download Document" key="download">
              <GridActionsCellItem
                onClick={async () => {
                  const { data } = await getDocumentDownloadLink({
                    variables: { id: params.row.documentId },
                  });
                  if (data?.getDocumentDownloadLink) {
                    window.open(data.getDocumentDownloadLink, "_blank");
                  }
                }}
                label="Download Document"
                icon={<Download />}
              />
            </Tooltip>
          );
        }

        // Delete action - driven by document's canDelete property
        if (params.row.canDelete && onRemoveDocument) {
          actions.push(
            <Tooltip
              title={`Remove ${
                params.row.sourceType === "invoice" ? "Invoice" : "Receipt"
              }${params.row.status === "pending" ? " (Pending)" : ""}`}
              key="delete"
            >
              <GridActionsCellItem
                onClick={() => {
                  onRemoveDocument(params.row.documentId, params.row.sourceType);
                }}
                label={`Remove ${params.row.sourceType === "invoice" ? "Invoice" : "Receipt"}`}
                icon={<Delete />}
              />
            </Tooltip>
          );
        }

        return actions;
      },
    },
  ];

  return (
    <>
      {!hideTitle && (
        <Typography variant="h6" sx={{ mb: 1 }}>
          {titleOverride || "Invoices & Attachments"}
        </Typography>
      )}

      {allDocuments.length > 0 && (
        <DataGrid
          sx={{
            height: 200,
            // Style pending rows differently - subtle blue tint
            "& .pending-row": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
              "&:hover": {
                backgroundColor: "rgba(25, 118, 210, 0.08)",
              },
            },
          }}
          rows={allDocuments}
          columns={columns}
          sortModel={[{ field: "title", sort: "asc" }]}
          hideFooter
          getRowClassName={(params) => (params.row.status === "pending" ? "pending-row" : "")}
        />
      )}
    </>
  );
};

// Legacy component name for backward compatibility
export const InvoicesTable = PaymentDocumentsTable;
